<script setup>
import { Link } from '@inertiajs/vue3'
import { ref } from 'vue'
import { Button } from '@/Components/ui/button'
import BrandLogo from '@/Components/BrandLogo.vue'
import AppHeader from '@/Components/AppHeader.vue'
import ImpersonateBanner from '@/Components/ImpersonateBanner.vue'
import ImpersonateModal from '@/Components/Modals/ImpersonateModal.vue'
import SupportChatModal from '@/Components/SupportChatModal.vue'
import HelpButton from '@/Components/HelpButton.vue'
import { home, becomeATutor, tutors, subjects, parents, faq, howItWorks } from '@/routes/index'

/**
 * The public frame: header, content, footer.
 *
 * The header and its drawer moved to AppHeader so the dashboards can use the
 * same one — signing in should change what the links point at, not the
 * furniture around them.
 */
const showImpersonateModal = ref(false)
const showSupportModal = ref(false)

const currentYear = new Date().getFullYear()
</script>

<template>
  <div class="min-h-screen bg-bg text-ink flex flex-col">
    <ImpersonateBanner />

    <AppHeader
      @open-support="showSupportModal = true"
      @open-impersonate="showImpersonateModal = true"
    />

    <!-- ==================== MAIN ==================== -->
    <main class="flex-1">
      <slot />
    </main>

    <!-- ==================== FOOTER ==================== -->
    <footer class="border-t border-line bg-surface-2">
      <div class="container-page py-14">
        <!--
          No call to action here. It sat in the footer of every page including
          the tutor listing and a tutor's own profile, where "Ready to find the
          right tutor?" is asking someone who is already doing exactly that.
          Pages that want a closing invitation carry their own.
        -->
        <div class="grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
          <div class="max-w-xs sm:col-span-2 lg:col-span-2">
            <BrandLogo />
            <p class="mt-4 text-sm leading-relaxed text-muted">
              Elite university tutors. Proven academic achievers. Personalised online tutoring that gets results.
            </p>
          </div>

          <div>
            <h2 class="font-sans text-sm font-bold tracking-normal text-ink">Learn</h2>
            <ul class="mt-4 space-y-2.5 text-sm">
              <li><Link :href="tutors()" class="text-muted transition-colors hover:text-brand">Our tutors</Link></li>
              <li><Link :href="subjects()" class="text-muted transition-colors hover:text-brand">Subjects</Link></li>
              <li><Link :href="howItWorks()" class="text-muted transition-colors hover:text-brand">How it works</Link></li>
            </ul>
          </div>

          <div>
            <h2 class="font-sans text-sm font-bold tracking-normal text-ink">Bindle</h2>
            <ul class="mt-4 space-y-2.5 text-sm">
              <li><Link :href="parents()" class="text-muted transition-colors hover:text-brand">For parents</Link></li>
              <li><Link :href="becomeATutor()" class="text-muted transition-colors hover:text-brand">Become a tutor</Link></li>
              <li><Link :href="faq()" class="text-muted transition-colors hover:text-brand">FAQ</Link></li>
              <li>
                <button type="button" class="text-muted transition-colors hover:text-brand" @click="showSupportModal = true">
                  Help &amp; support
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h2 class="font-sans text-sm font-bold tracking-normal text-ink">Legal</h2>
            <ul class="mt-4 space-y-2.5 text-sm">
              <li><Link href="/privacy-policy" class="text-muted transition-colors hover:text-brand">Privacy policy</Link></li>
              <li><Link href="/terms-of-service" class="text-muted transition-colors hover:text-brand">Tutor terms</Link></li>
              <li><Link href="/student-terms" class="text-muted transition-colors hover:text-brand">Student terms</Link></li>
            </ul>
          </div>
        </div>

        <div class="mt-12 flex flex-col gap-3 border-t border-line pt-7 text-sm text-muted sm:flex-row sm:items-center sm:justify-between">
          <p>© {{ currentYear }} Bindle. All rights reserved.</p>
          <p>Registered in England &amp; Wales</p>
        </div>
      </div>
    </footer>

    <ImpersonateModal :show="showImpersonateModal" @close="showImpersonateModal = false" />

    <HelpButton />

    <!-- Still mounted for the footer and menu links that open support directly -->
    <SupportChatModal :open="showSupportModal" @close="showSupportModal = false" />
  </div>
</template>
